/**
 * UTM & Marketing Attribution Utilities
 * Uses localStorage so attribution persists across browsing sessions
 * until explicitly cleared on logout.
 */

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'fbclid',
  'click_id',
  'wl_click_id',
  'wl_offer_id',
  'clickId',
];

const ATTRIBUTED_SESSION_KEY = 'utm_attributed_session';

/**
 * True when this tab already captured a tracked link (utm_source) earlier in the session.
 */
export const hasAttributedSession = () => {
  try {
    return Boolean(sessionStorage.getItem(ATTRIBUTED_SESSION_KEY));
  } catch (e) {
    return false;
  }
};

/**
 * True when the current URL itself carries tracking parameters.
 */
export const urlHasAttribution = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    return Boolean(params.get('utm_source') || params.get('click_id') || params.get('offerid'));
  } catch (e) {
    return false;
  }
};


export const captureUtmParams = () => {
  if (typeof window === 'undefined') return;

  try {
    const params = new URLSearchParams(window.location.search);

    const incomingSource = params.get('utm_source');
    const incomingFbclid = params.get('fbclid');
    const incomingClickId = params.get('click_id') || params.get('utm_term');

    if (incomingFbclid) {
      localStorage.setItem('fbclid', incomingFbclid);
    } else if (incomingSource) {
      localStorage.removeItem('fbclid');
    }

    if (incomingClickId) {
      localStorage.setItem('wl_click_id', incomingClickId);
    }

    if (incomingSource) {
      localStorage.setItem('utm_source', incomingSource);
      localStorage.setItem('utm_medium', params.get('utm_medium') || '');
      localStorage.setItem('utm_campaign', params.get('utm_campaign') || '');
      localStorage.setItem('utm_term', params.get('utm_term') || '');
      localStorage.setItem('utm_content', params.get('utm_content') || '');
      // Marks this tab as having arrived through a tracked link, so the Apply Now
      // page keeps the attribution instead of treating it as stale.
      sessionStorage.setItem(ATTRIBUTED_SESSION_KEY, '1');
    }
  } catch (e) {
    console.warn('[UTM] Failed to capture UTM parameters:', e?.message || e);
  }
};


export const getUtmParams = () => {
  if (typeof window === 'undefined') {
    return { utm_source: '', utm_medium: '', utm_campaign: '', utm_term: '', utm_content: '' };
  }

  try {
    const storedSource = localStorage.getItem('utm_source');
    if (storedSource) {
      return {
        utm_source: storedSource,
        utm_medium: localStorage.getItem('utm_medium') || '',
        utm_campaign: localStorage.getItem('utm_campaign') || '',
        utm_term: localStorage.getItem('utm_term') || '',
        utm_content: localStorage.getItem('utm_content') || ''
      };
    }

    // Fallback directly from URL if not yet captured in localStorage
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_term: params.get('utm_term') || '',
      utm_content: params.get('utm_content') || ''
    };
  } catch (e) {
    return { utm_source: '', utm_medium: '', utm_campaign: '', utm_term: '', utm_content: '' };
  }
};

/**
 * Returns the stored utm_source (uppercased, trimmed).
 */
export const getStoredUtmSource = () => {
  const { utm_source } = getUtmParams();
  return (utm_source || '').trim().toUpperCase();
};

/**
 * Clears all UTM and campaign tracking keys from sessionStorage and localStorage.
 */
export const clearUtmParams = () => {

  if (typeof window === 'undefined') return;
  // localStorage.clear();
  // sessionStorage.clear();
  UTM_KEYS.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) { }
  });
};
